<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import Pager from '@/helpers/Pager';
import { Form } from '@/helpers/Form';
import { Prop, Watch } from '@/helpers/Decorators';
import LinkDocumentsModal from './modals/LinkDocumentsModal.vue';
import DocumentRelationshipsService from '../services/DocumentRelationshipsService';
import { scrollRedirect } from '@/components/common/dynamic-grid/helpers/scrollRedirect';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import GridService, { ColumnDefinitionModel } from '@/modules/core/common/services/GridService';
import { FieldModel } from '@/modules/low-code/services/FieldConfigsService';

@Options({
    name: 'RelatedDocuments',
    components: {
        LinkDocumentsModal,
    },
})
export default class RelatedDocuments extends Vue
{
    @Prop() public parentType: string;
    @Prop() public documentId: string;
    @Prop({ default: false }) public allowBindings: boolean;
    @Prop() public blueprintId: string;
    @Prop({ default: '' }) public blueprintName: string;
    @Prop({ default: NeverChoice.Never }) public gridHeaderSetting: AlwaysChoice.Always | NeverChoice.Never;
    @Prop({ default: false }) public showFilters: boolean;
    @Prop({ default: true }) public showExport: boolean;
    @Prop({ default: {} }) public title: Record<string, string>;
    @Prop({ default: null }) public columns: Record<string, FieldModel>;

    public url: string = 'documents-related';
    public actionObjectType: string = 'related-document';
    public pager = new Pager(1, 10, 'Id', 'DESC');
    public filter = Form.create({});
    public filterBuilder: Record<string, unknown> = {};
    public canLink: boolean = false;
    public extraParams: any = {};
    public computedColumns: ColumnDefinitionModel | null = null;

    public get filtersDone(): boolean
    {
        return this.$store.state.common.grid.instances?.[this.viewStateKey]?.filtersInitialized ?? false;
    }

    public get endpoint(): string
    {
        return `${this.parentType}/${this.documentId}/${this.url}`;
    }

    public get linkDocumentsEventName(): string
    {
        return this.blueprintId ? `link-documents:${this.blueprintId}` : 'link-documents';
    }

    public get viewStateKey(): string
    {
        return this.blueprintId ? `${this.endpoint}:${this.blueprintId}` : this.endpoint;
    }

    public get refetchEmitId(): string
    {
        return this.blueprintId ? `related-documents:${this.blueprintId}` : `related-documents:${this.endpoint}`;
    }

    public get fileName(): string
    {
        return `${this.parentType}_${this.url}`;
    }

    public get isShowHeader(): boolean
    {
        return this.gridHeaderSetting === AlwaysChoice.Always;
    }

    public get isShowFilters(): boolean
    {
        return this.showFilters;
    }

    public get isShowExport(): boolean
    {
        return this.showExport;
    }

    @Watch('columns', { immediate: true, deep: true })
    public async applyColumnsConfiguration(): Promise<void>
    {
        if (!this.columns || Object.keys(this.columns).length === 0)
        {
            this.computedColumns = null;

            return;
        }

        try
        {
            const apiColumns = await GridService.fetchColumnsDefinitionNew(
                this.endpoint,
                null,
                { blueprintId: this.blueprintId }
            );

            const modifiedColumns: ColumnDefinitionModel = JSON.parse(JSON.stringify(apiColumns));

            for (const [key, fieldConfig] of Object.entries(this.columns))
            {
                if (modifiedColumns[key])
                {
                    modifiedColumns[key].visibility = fieldConfig.visibility;
                    modifiedColumns[key].order = fieldConfig.order;
                }
            }

            this.computedColumns = JSON.parse(JSON.stringify(modifiedColumns));
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
            this.computedColumns = null;
        }
    }

    public async created(): Promise<void>
    {
        this.canLink = await DocumentRelationshipsService.canLink(this.documentId, this.parentType, this.blueprintId);
    }

    public redirectToDocument(url: string): void
    {
        url && this.$router.push(url);
    }

    public onRowClick(item: any): void
    {
        if (!item.result.publicId)
            return;

        this.$router.push({
            name: 'module-form-details',
            params: { licence: item.result.licence, publicId: item.result.publicId },
        });
    }

    public onScrollClick(item: any): void
    {
        if (!item.result.publicId) return;

        scrollRedirect({
            name: 'module-form-details',
            params: { licence: item.result.licence, publicId: item.result.publicId },
        });
    }

    public overwriteFilter(event: { key: string; value: any; period: boolean, amount: boolean }): void
    {
        if (event.period)
        {
            this.filterBuilder[`${event.key}.FromUtc`] = event.value;
            this.filterBuilder[`${event.key}.ToUtc`] = event.value;
            this.filterBuilder[`${event.key}.DaysBeforeNow`] = event.value;
            this.filterBuilder[`${event.key}.DaysAfterNow`] = event.value;
        }
        else if (event.amount)
        {
            this.filterBuilder[`${event.key}.FromUtc`] = event.value;
            this.filterBuilder[`${event.key}.ToUtc`] = event.value;
        }
        else this.filterBuilder[event.key] = event.value;
    }

    public initializationChanged(newValue: boolean): void
    {
        if (newValue)
        {
            this.filter.setInitialValues(this.filterBuilder);
            this.filter.withData(this.filterBuilder);
        }
    }

    @Watch('filtersDone')
    public onFiltersDoneChanged(newValue: boolean): void
    {
        this.initializationChanged(newValue);
    }

    public beforeMount(): void
    {
        this.$events.$on('refetchRelatedDocuments', () =>
        {
            if (this.$refs.grid)
            {
                (this.$refs.grid as any).fetchData();
            }
        });
    }

    public beforeUnmount(): void
    {
        this.$events.$off('refetchRelatedDocuments');
        // ALWAYS CLEAR FILTER INITIALIZATION WHEN LEAVING A COMPONENT IN MODULE SCOPE
        this.$store.commit('common/grid/SET_INITIALIZATION', false);
    }

    public async unlinkDocuments(item: any): Promise<void>
    {
        try
        {
            await DocumentRelationshipsService.unlinkDocuments(this.documentId, item.result.publicId);
            this.$alert.success(this.$t('[[[Pomyślnie usunięto powiązanie między dokumentami]]]'));

            // Refresh the grid data
            if (this.$refs.grid)
            {
                (this.$refs.grid as any).fetchData();
            }
        }
        catch (ex)
        {
            this.handleException(ex, {
                422: (ex: any) => this.$alert.warning(ex.data?.errors?.publicId?.[0] || ex.data?.message || ex.message)
            });
        }
    }
}
</script>

<template>
    <div>
        <DynamicGrid ref="grid" :pager="pager" :filter="filter" :endpoint="endpoint" :view-state-key="viewStateKey" :manual-init="true" :emit-id="refetchEmitId" table-type="related-documents" :extra-params="{ blueprintId: blueprintId }" :row-click="onRowClick" :scroll-click="onScrollClick" :columns="computedColumns">
            <!-- Header -->
            <template v-if="canLink || isShowHeader" #header>
                <IdeoPanel :column="false" :stretch="false" :start="desktop">
                    <template v-if="canLink" #start>
                        <ActionButton
                            variant="primary"
                            icon="fas fa-plus"
                            :text="$t('[[[Powiąż dokument]]]')"
                            @click="$events.$emit(linkDocumentsEventName)"
                        />
                    </template>
                    <template #default>
                        <div v-if="isShowHeader" class="flex-fill d-flex align-items-center justify-content-between gap-4">
                            <h5 class="flex-grow-0 flex-shrink-0 m-0" style="line-height: 36px;">{{ $i18n.currentTranslation(title) }}</h5>
                            <NewFiltersWrapper v-if="isShowFilters" class="ms-auto" :filter="filter" :url="url" :minimalist="true" :pager="pager" :view-state-key="viewStateKey" :emit-id="refetchEmitId">
                                <DynamicFilters
                                    :request-url="url"
                                    :parent-type="parentType"
                                    :parent-id="documentId"
                                    :filter="filter"
                                    :extra-params="{ blueprintId: blueprintId }"
                                    :view-state-key="viewStateKey"
                                    @filter-update="overwriteFilter"
                                />
                            </NewFiltersWrapper>
                        </div>
                    </template>
                    <template v-if="isShowExport" #end>
                        <!-- :actions="actions" -->
                        <ExportManager
                            :excel="{ endpoint, extraParams: { blueprintId: blueprintId } }"
                            :filter="filter"
                            :pager="pager"
                        />
                    </template>
                </IdeoPanel>
            </template>

            <!-- Actions -->
            <template #actions="{ item }">
                <RowActions
                    :id="item.result.publicId"
                    :endpoint="`${item.result.licence}/${item.result?.publicId}/actions`"
                    :url="item.result.licence"
                    :except="[
                        'document-notifications-create',
                        'canManageParticipants',
                        'canUploadOneDrive',
                        'bpmn-set-targets',
                        'canSetPrivate',
                    ]"
                >
                    <IdeoDropdownItemButton
                        v-if="item?.result?.licence"
                        @click="onRowClick(item)"
                    >
                        <i class="fas fa-square-list"></i>
                        <span>{{ $t('[[[Szczegóły]]]') }}</span>
                    </IdeoDropdownItemButton>
                    <IdeoDropdownItemButton
                        v-if="canLink"
                        :message="$t('[[[Czy na pewno chcesz usunąć powiązanie między dokumentami?]]]')"
                        :value="item"
                        @confirm="unlinkDocuments"
                    >
                        <i class="fas fa-unlink"></i>
                        <span>{{ $t('[[[Usuń powiązanie]]]') }}</span>
                    </IdeoDropdownItemButton>
                </RowActions>
            </template>

            <!-- Modal -->
            <template #modal>
                <LinkDocumentsModal :document-id="documentId" :licence="parentType" :blueprint-id="blueprintId" :view-state-key="viewStateKey" :emit-id="refetchEmitId" />
            </template>
        </DynamicGrid>
        <Portal v-if="blueprintName" :to="`tab-counter-${blueprintName}`">
            {{ pager.getTotalRows() }}
        </Portal>
    </div>
</template>
